import moment from "moment";
import { applyDefaultIdIssueCountry } from "./clientIdIssueCountry";

export const HKID_REGEX = /^[A-Z]{1,2}[0-9]{6}\([0-9A]\)$/;
export const HKBR_REGEX = /^\d{8}(-\d{3})?$/;
export const DUMMY_BR_REGEX = /^[CF]\d{7}$/;
export const CI_NO_MAX_LENGTH = 50;
export const CI_NO_REGEX = /^[A-Za-z0-9\-\/\s]+$/;

export const BR_NO_ID_TYPE = "BR No.";
export const CI_NO_ID_TYPE = "CI No.";

export const ID_TYPE_PLACEHOLDER = {
  HKID: "A123456(7)",
  CNID: "Mainland China ID No.",
  "Travel Permit": "Travel Permit No.",
  "MID No.": "Macau ID No.",
  "BR No.": "12345678 or C0012345",
  "CI No.": "Certificate of Incorporation No.",
  Passport: "Passport No.",
  Other: "ID No.",
};

export const getClientIdPlaceholder = (idType) =>
  ID_TYPE_PLACEHOLDER[idType] ?? "ID No.";

export const NO_ID_PROVIDED = "No ID provided";

export const isNoIdProvided = (idType) => idType === NO_ID_PROVIDED;

export const isMainIdRequired = (idType) => !isNoIdProvided(idType);

export const isBrIdType = (idType) => idType === BR_NO_ID_TYPE;

export const isCiIdType = (idType) => idType === CI_NO_ID_TYPE;

export const isValidBrNumber = (value) => {
  const normalized = normalizeIdValue(value);
  return HKBR_REGEX.test(normalized) || DUMMY_BR_REGEX.test(normalized);
};

export const isMainIdValidationActive = (formValues) => {
  const type = formValues?.type;
  if (type !== "INDIVIDUAL" && type !== "CORPORATE") return false;
  return isMainIdRequired(formValues?.idType);
};

export const isClientWithOtherIds = (type) =>
  type === "INDIVIDUAL" || type === "CORPORATE";

export const validateClientIdFormat = (value, idType, t) => {
  if (idType === "HKID" && !HKID_REGEX.test(value)) {
    return t("It is not a valid HKID");
  }
  if (isBrIdType(idType) && !isValidBrNumber(value)) {
    return t("It is not a valid BR number");
  }
  if (isCiIdType(idType)) {
    const trimmed = String(value ?? "").trim();
    if (trimmed.length > CI_NO_MAX_LENGTH) {
      return t("CI No. is too long");
    }
    if (trimmed && !CI_NO_REGEX.test(trimmed)) {
      return t("CI No. contains invalid characters");
    }
  }
  return null;
};

export const isOtherIdsSectionVisible = (formValues) =>
  isClientWithOtherIds(formValues?.type) &&
  isMainIdRequired(formValues?.idType);

export const normalizeIdValue = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

export const isRequiredValueMissing = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return !moment(value).isValid();
  return !String(value).trim();
};

export const isIdExpiryRequired = (idType) =>
  Boolean(idType) &&
  idType !== "HKID" &&
  idType !== "Other" &&
  idType !== CI_NO_ID_TYPE &&
  idType !== NO_ID_PROVIDED;

// Same rule as the checkIdentityExpired cron: non-HKID, and the expiry
// calendar day is before today. The expiry day itself is still valid.
export const isIdentityDocumentExpired = (
  { idType, idDocExpiryDate } = {},
  referenceDate = new Date(),
) => {
  if (idType === "HKID" || !idDocExpiryDate) return false;
  // Form visibleOnly passes (values, accessRight). Ignore anything that is not a date.
  const reference =
    referenceDate instanceof Date ? referenceDate : new Date();
  return moment(idDocExpiryDate)
    .startOf("day")
    .isBefore(moment(reference).startOf("day"));
};

export const formatIdentityExpiredYn = (identityExpired) =>
  identityExpired ? "Y" : "N";

export const IDENTITY_EXPIRED_FILTER_VALUES = [
  { value: "expired", label: "Expired" },
  { value: "valid", label: "Not expired" },
];

// Mongo clause matching isIdentityDocumentExpired. "valid" also includes
// clients with no expiry date and HKID, which the cron never flags.
export const buildIdentityExpiredListFilter = (
  value,
  referenceDate = new Date(),
) => {
  const startOfToday = moment(referenceDate).startOf("day").toDate();
  if (value === "expired") {
    return {
      idType: { $ne: "HKID" },
      idDocExpiryDate: { $exists: true, $ne: null, $lt: startOfToday },
    };
  }
  if (value === "valid") {
    return {
      $or: [
        { idType: "HKID" },
        { idDocExpiryDate: null },
        { idDocExpiryDate: { $exists: false } },
        { idDocExpiryDate: { $gte: startOfToday } },
      ],
    };
  }
  return null;
};

export const handleNoIdProvidedChange = (checked, form) => {
  if (checked) {
    if (!isNoIdProvided(form.values?.idType)) {
      form._previousIdType = form.values?.idType || "HKID";
    }
    form.setFieldValue("idType", NO_ID_PROVIDED);
    form.setFieldValue("id", "");
    form.setFieldValue("idIssueCountry", "");
    form.setFieldValue("idDocExpiryDate", null);
    form.setFieldValue("otherIds", []);
    return;
  }

  const restoredIdType = form._previousIdType || "HKID";
  form.setFieldValue("idType", restoredIdType);
  applyDefaultIdIssueCountry(restoredIdType, form);
};

export const validateOtherIdRow = (row, t) => {
  const errors = {};
  const requiredMessage = (label) =>
    t ? t(`${label} is required`) : `${label} is required`;

  if (isRequiredValueMissing(row?.idType)) {
    errors.idType = requiredMessage("ID Type");
  }
  if (isRequiredValueMissing(row?.id)) {
    errors.id = requiredMessage("ID");
  } else {
    const formatError = validateClientIdFormat(row.id, row?.idType, t);
    if (formatError) errors.id = formatError;
  }
  if (isRequiredValueMissing(row?.idIssueCountry)) {
    errors.idIssueCountry = requiredMessage("ID Issue Country");
  }
  if (
    isIdExpiryRequired(row?.idType) &&
    isRequiredValueMissing(row?.idDocExpiryDate)
  ) {
    errors.idDocExpiryDate = requiredMessage("Identity Doc Expiry Date");
  }

  return errors;
};

export const getAllClientIdValues = (formValues) => {
  const ids = [];
  if (formValues?.id) {
    ids.push(normalizeIdValue(formValues.id));
  }
  for (const row of formValues?.otherIds ?? []) {
    if (row?.id) ids.push(normalizeIdValue(row.id));
  }
  return ids;
};

export const validateOtherIds = (otherIds, formValues, t) => {
  if (
    !formValues ||
    !isClientWithOtherIds(formValues.type) ||
    isNoIdProvided(formValues.idType)
  ) {
    return null;
  }
  const rows = otherIds ?? [];
  if (rows.length === 0) return null;

  for (let i = 0; i < rows.length; i++) {
    const rowErrors = validateOtherIdRow(rows[i], t);
    const firstError = Object.values(rowErrors)[0];
    if (firstError) {
      return t
        ? `${t("Other ID")} ${i + 1}: ${firstError}`
        : `Other ID ${i + 1}: ${firstError}`;
    }
  }

  const allIds = getAllClientIdValues({ ...formValues, otherIds });
  const seen = new Set();
  for (const id of allIds) {
    if (!id) continue;
    if (seen.has(id)) {
      return t
        ? t("Duplicate ID found within this client")
        : "Duplicate ID found within this client";
    }
    seen.add(id);
  }

  return null;
};

export const buildOtherIdRowValidate = (t) => {
  return {
    idType: (value) =>
      isRequiredValueMissing(value) ? t("ID Type is required") : null,
    id: (value, formValues) => {
      if (isRequiredValueMissing(value)) return t("ID is required");
      return validateClientIdFormat(value, formValues?.idType, t);
    },
    idIssueCountry: (value) =>
      isRequiredValueMissing(value) ? t("ID Issue Country is required") : null,
    idDocExpiryDate: (value, formValues) => {
      if (!isIdExpiryRequired(formValues?.idType)) return null;
      if (isRequiredValueMissing(value)) {
        return t("Identity Doc Expiry Date is required");
      }
      return null;
    },
  };
};

export const promoteIdentityToMain = (formValues, rowIndex) => {
  const otherIds = [...(formValues.otherIds ?? [])];
  const promoted = otherIds[rowIndex];
  if (!promoted) return formValues;

  const formerMain = {
    idType: formValues.idType,
    id: formValues.id,
    idIssueCountry: formValues.idIssueCountry,
    idDocExpiryDate: formValues.idDocExpiryDate,
  };

  otherIds[rowIndex] = formerMain;

  return {
    ...formValues,
    idType: promoted.idType,
    id: promoted.id,
    idIssueCountry: promoted.idIssueCountry,
    idDocExpiryDate: promoted.idDocExpiryDate,
    otherIds,
  };
};

export const formatOtherIdsSummary = (otherIds) => {
  const count = otherIds?.length ?? 0;
  if (count === 0) return "-";
  return `${count} record(s)`;
};
